import {
	Check,
	CreditCard,
	FileBarChart,
	FileText,
	Fingerprint,
	HelpCircle,
	Key,
	Laptop,
	LinkIcon,
	LogOut,
	Mail,
	Moon,
	Palette,
	Puzzle,
	ShieldCheck,
	Sun,
	UserCog,
} from 'lucide-react'
import { Link, useFetcher, useRouteLoaderData, useSubmit } from 'react-router'
import { Avatar, AvatarFallback } from '#app/components/ui/avatar.tsx'
import { Button } from '#app/components/ui/button.tsx'
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuLabel,
	DropdownMenuSeparator,
	DropdownMenuSub,
	DropdownMenuSubContent,
	DropdownMenuSubTrigger,
	DropdownMenuTrigger,
} from '#app/components/ui/dropdown-menu.tsx'
import { useOptimisticThemeMode } from '#app/routes/resources+/theme-switch.tsx'
import { useRequestInfo } from '#app/utils/request-info.ts'
import { type Theme } from '#app/utils/theme.server.ts'
import { useUser } from '#app/utils/user.ts'

function getInitials(name: string | null | undefined, fallback: string): string {
	const source = (name ?? fallback).trim()
	if (!source) return '?'
	const parts = source.split(/\s+/).filter(Boolean)
	if (parts.length >= 2) {
		return (parts[0]![0]! + parts[1]![0]!).toUpperCase()
	}
	return source.slice(0, 2).toUpperCase()
}

const THEME_OPTIONS: Array<{ value: Theme | 'system'; label: string; Icon: typeof Sun }> = [
	{ value: 'light', label: 'Light', Icon: Sun },
	{ value: 'dark', label: 'Dark', Icon: Moon },
	{ value: 'system', label: 'System', Icon: Laptop },
]

type RootData = {
	currentBaId?: string | null
}

export function NavUser() {
	const user = useUser()
	const requestInfo = useRequestInfo()
	const optimisticMode = useOptimisticThemeMode()
	const currentMode = optimisticMode ?? requestInfo.userPrefs.theme ?? 'system'
	const themeFetcher = useFetcher()
	const submit = useSubmit()
	const root = useRouteLoaderData('root') as RootData | undefined
	const baId = root?.currentBaId ?? null

	const displayName = user.name ?? user.username ?? user.email
	const initials = getInitials(user.name ?? user.username, user.email)

	function setTheme(theme: Theme | 'system') {
		void themeFetcher.submit(
			{ theme },
			{ method: 'POST', action: '/resources/theme-switch' },
		)
	}

	function logOut() {
		void submit(null, { method: 'POST', action: '/logout' })
	}

	// modal={false} skips Radix's react-remove-scroll body lock. The
	// scrollbar-gutter:stable rule on <html> (a029d99) handles the vertical
	// case, but react-remove-scroll ALSO compensates position:fixed elements
	// (top nav, drawer) — which double-shifts the fixed SiteTopNav that
	// hosts NavUser on the marketing pages. An account dropdown has no
	// reason to trap scroll; non-modal preserves outside-click + Escape
	// close semantics while eliminating the shift.
	return (
		<DropdownMenu modal={false}>
			<DropdownMenuTrigger asChild>
				<Button
					variant="outline"
					size="icon"
					aria-label="Open account menu"
					className="size-9"
				>
					<Avatar className="size-6">
						<AvatarFallback className="text-[10px] font-medium">
							{initials}
						</AvatarFallback>
					</Avatar>
				</Button>
			</DropdownMenuTrigger>
			<DropdownMenuContent align="end" className="w-64">
				<DropdownMenuLabel className="p-0 font-normal">
					<div className="flex items-center gap-2 px-2 py-1.5 text-sm">
						<Avatar className="size-8">
							<AvatarFallback className="text-xs font-medium">
								{initials}
							</AvatarFallback>
						</Avatar>
						<div className="grid flex-1 text-left text-sm leading-tight">
							<span className="truncate font-semibold">{displayName}</span>
							<span className="truncate text-xs text-muted-foreground">
								{user.email}
							</span>
						</div>
					</div>
				</DropdownMenuLabel>
				<DropdownMenuSeparator />
				<DropdownMenuItem asChild>
					<Link to="/settings/profile">
						<UserCog className="mr-2 size-4" />
						Profile
					</Link>
				</DropdownMenuItem>
				<DropdownMenuItem asChild>
					<Link to="/settings/profile/password">
						<Key className="mr-2 size-4" />
						Password
					</Link>
				</DropdownMenuItem>
				<DropdownMenuItem asChild>
					<Link to="/settings/profile/change-email">
						<Mail className="mr-2 size-4" />
						Change email
					</Link>
				</DropdownMenuItem>
				<DropdownMenuItem asChild>
					<Link to="/settings/profile/passkeys">
						<Fingerprint className="mr-2 size-4" />
						Passkeys
					</Link>
				</DropdownMenuItem>
				<DropdownMenuItem asChild>
					<Link to="/settings/profile/two-factor">
						<ShieldCheck className="mr-2 size-4" />
						Two-factor
					</Link>
				</DropdownMenuItem>
				<DropdownMenuItem asChild>
					<Link to="/settings/profile/connections">
						<LinkIcon className="mr-2 size-4" />
						Connections
					</Link>
				</DropdownMenuItem>
				<DropdownMenuItem asChild>
					<Link to="/settings/profile/apps">
						<Puzzle className="mr-2 size-4" />
						Apps
					</Link>
				</DropdownMenuItem>
				<DropdownMenuSeparator />
				<DropdownMenuItem asChild>
					<Link to={baId ? `/billing/${baId}` : '/billing'}>
						<CreditCard className="mr-2 size-4" />
						Billing
					</Link>
				</DropdownMenuItem>
				<DropdownMenuItem asChild>
					<Link to="/partner/invoices">
						<FileBarChart className="mr-2 size-4" />
						Partner invoices
					</Link>
				</DropdownMenuItem>
				<DropdownMenuSeparator />
				<DropdownMenuSub>
					<DropdownMenuSubTrigger>
						<Palette className="mr-2 size-4" />
						Theme
					</DropdownMenuSubTrigger>
					<DropdownMenuSubContent>
						{THEME_OPTIONS.map(({ value, label, Icon }) => {
							const isCurrent = currentMode === value
							return (
								<DropdownMenuItem key={value} onSelect={() => setTheme(value)}>
									<Icon className="mr-2 size-4" />
									<span className="flex-1">{label}</span>
									{isCurrent && <Check className="size-4" />}
								</DropdownMenuItem>
							)
						})}
					</DropdownMenuSubContent>
				</DropdownMenuSub>
				<DropdownMenuItem asChild>
					<Link to="/docs">
						<FileText className="mr-2 size-4" />
						Docs
					</Link>
				</DropdownMenuItem>
				<DropdownMenuItem asChild>
					<Link to="/help">
						<HelpCircle className="mr-2 size-4" />
						Help & Support
					</Link>
				</DropdownMenuItem>
				<DropdownMenuSeparator />
				<DropdownMenuItem onSelect={logOut}>
					<LogOut className="mr-2 size-4" />
					Log out
				</DropdownMenuItem>
			</DropdownMenuContent>
		</DropdownMenu>
	)
}
